import { AgentInteractionStatus } from "@/common/constants/callSession";
import { AgentPermissionsState } from "@/common/stores/persistingStores/agentPermissions";
import { useAgentPermissionsStore } from "@/common/stores/persistingStores/agentPermissions/agentPermissionsStore";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import {
	MessagingInviteState,
	useMessagingInviteSessionStore,
} from "@/common/stores/nonpersistingStores/messagingInvite";
import { EmailTrustedEndpoint } from "@/repository/models/message";
import {
	EmailEndpointsKey,
	fetchEmailTrustedEndpoints,
} from "@/repository/queryFns";
import { useQuery } from "@tanstack/react-query";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import { useAgentInteractionState } from "../agent";

export interface OutboundHookData {
	isOutboundPossible: boolean;
	isOutboundAllowed: boolean;
	trustedEndpoints: EmailTrustedEndpoint[];
	isReopenPossible: boolean;
	hasAnyOutboundRightEndpoints: boolean;
}

export const useEmailOutbound = (): OutboundHookData => {
	const hasOutboundRights = useAgentPermissionsStore(
		(state: AgentPermissionsState) => state.hasOutboundRights
	);
	const endpointsResult = useQuery({
		queryKey: [EmailEndpointsKey],
		queryFn: fetchEmailTrustedEndpoints,
	});

	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const { agentHasInteractionInOffering } = useAgentInteractionState();

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const messagingInviteSession = useMessagingInviteSessionStore(
		(state: MessagingInviteState) => state.messagingSession
	);

	const isOutboundAllowed =
		(!callSession || callSession.state !== AgentInteractionStatus.Offering) &&
		(!messagingInviteSession ||
			messagingInviteSession.state !== AgentInteractionStatus.Offering);

	const outboundRightQueueIds = queues
		.filter((x) => x.hasOutboundRight)
		.map((x) => x.id);

	const outboundRightTrustedEndpoints = endpointsResult.data?.filter((x) =>
		outboundRightQueueIds.includes(x.queueId)
	);

	const isOutboundPossible =
		hasOutboundRights && !!outboundRightTrustedEndpoints?.length;

	const hasAnyOutboundRightEndpoints =
		(outboundRightTrustedEndpoints?.length ?? 0) > 0;

	return {
		isOutboundAllowed,
		isOutboundPossible,
		trustedEndpoints: outboundRightTrustedEndpoints || [],
		isReopenPossible: !agentHasInteractionInOffering,
		hasAnyOutboundRightEndpoints,
	};
};
